// Onboarding wizard (audit #4) — 5-step modal shown on first load.
// Tracks completion in localStorage so it stops appearing once done.
import React from 'react';
import { CheckCircle, ChevronRight, X } from 'lucide-react';

const STEPS = [
  { id: 'profile',  title: 'Welcome',           body: 'Quick 60-second setup — load your book, hook up SMS, send a test text. Then you\'re live.' },
  { id: 'load',     title: 'Load your book',    body: 'Paste a commission statement OR load demo data to explore. Without data, the dashboard stays empty.', action: { label: 'Go to Home', tab: 'home' } },
  { id: 'sms',      title: 'SMS verification',  body: 'Enter your business info so carriers approve your outbound texts. Twilio submits automatically; carrier review takes 1–5 days. Until then the platform shared brand keeps SMS working.', action: { label: 'Enter business info', tab: 'settings' } },
  { id: 'agents',   title: 'Turn on AI agents', body: 'Manager AI, Lead Follow-up, Health Client Agent — flip them on in Settings → AI Agents.', action: { label: 'AI Agents', tab: 'ai_agents' } },
  { id: 'meta',     title: 'Meta Ads (optional)', body: 'Already running FB/IG ads? Connect your Page so leads flow in automatically and the AI texts them.', action: { label: 'Meta Ads', tab: 'meta_ads' } },
];

const STORAGE_KEY = 'crm:onboarding:v2';

export function shouldShowOnboarding() {
  try { return localStorage.getItem(STORAGE_KEY) !== 'completed' && localStorage.getItem(STORAGE_KEY) !== 'dismissed'; }
  catch { return true; }
}

export default function OnboardingWizard({ onClose, onGotoTab = () => {} }) {
  const [step, setStep] = React.useState(0);

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else complete();
  };
  const complete = () => {
    try { localStorage.setItem(STORAGE_KEY, 'completed'); } catch {}
    onClose();
  };
  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, 'dismissed'); } catch {}
    onClose();
  };

  const s = STEPS[step];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onClick={dismiss}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Setup · Step {step + 1} of {STEPS.length}</div>
            <h2 className="text-lg font-bold mt-0.5">{s.title}</h2>
          </div>
          <button onClick={dismiss} className="text-slate-300 hover:text-white"><X size={18}/></button>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-slate-700">{s.body}</p>
          <div className="mt-4 flex gap-2 flex-wrap">
            {s.action && (
              <button onClick={() => { onGotoTab(s.action.tab); }} className="bg-white border-2 border-slate-900 text-slate-900 text-sm font-bold px-4 py-2 rounded-md">{s.action.label} →</button>
            )}
            <button onClick={next} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-1.5">
              {step === STEPS.length - 1 ? 'Finish' : 'Next'} <ChevronRight size={14}/>
            </button>
            <button onClick={dismiss} className="text-sm text-slate-500 hover:text-slate-900 px-3 py-2">Skip setup</button>
          </div>
        </div>

        {/* Step progress dots */}
        <div className="px-6 pb-4 flex gap-1">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1 rounded-full ${i <= step ? 'bg-emerald-500' : 'bg-slate-200'}`}/>
          ))}
        </div>
      </div>
    </div>
  );
}
